import { getUiLocale, uiText } from '../i18n/uiText';
export const DATE_FORMATS = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: '2-digit', month: 'short', year: 'numeric' },
  datetime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  weekday: { weekday: 'short', day: '2-digit', month: 'short' },
};

export function formatDate(value, format = 'short') {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getUiLocale() === 'az' ? 'az-AZ' : 'ru-RU', DATE_FORMATS[format] || DATE_FORMATS.short).format(date);
}

// Backend DateTime values are UTC even when a legacy response has no suffix.
// Keep calendar-only due dates separate from instants.
export function parseUtcInstant(value) {
  if (!value) return null;
  const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatNotificationTime(value) {
  const date = parseUtcInstant(value);
  return date ? `${new Intl.DateTimeFormat(getUiLocale() === 'az' ? 'az-AZ' : 'ru-RU', { ...DATE_FORMATS.datetime, timeZone: 'Europe/Moscow' }).format(date)} ${uiText('МСК')}` : '—';
}

export function moscowDay(value) {
  const date = parseUtcInstant(value);
  return date ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date) : '';
}

export function formatRelativeDueDate(value) {
  if (!value) return uiText('Без срока');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return uiText('Без срока');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return uiText('Сегодня');
  if (diff === 1) return uiText('Завтра');
  if (diff === -1) return uiText('Вчера');
  if (diff < 0) return uiText('Просрочено на {days} дн.', { days: Math.abs(diff) });
  return uiText('Через {days} дн.', { days: diff });
}

const STATUS_LABELS = {
  audit: {
    draft: uiText("Черновик"),
    not_started: uiText("Не начат"),
    program_sent: uiText("Программа отправлена"),
    program_confirmed: uiText("Программа согласована"),
    in_progress: uiText("В работе"),
    ready: uiText("Готов к закрытию"),
    awaiting_closure: uiText("Готов к закрытию"),
    completed: uiText("Завершён"),
    closed: uiText("Закрыт"),
    suspended: uiText("Приостановлен"),
    cancelled: uiText("Отменён"),
  },
  issue: {
    open: uiText("Открыто"),
    in_progress: uiText("В работе"),
    resolved: uiText("Решено"),
    closed: uiText("Закрыто"),
  },
  request: {
    draft: uiText("Черновик"),
    submitted: uiText("Отправлена"),
    in_review: uiText("На рассмотрении"),
    awaiting_customer: uiText("Ожидает клиента"),
    approved: uiText("Одобрена"),
    rejected: uiText("Отклонена"),
    converted: uiText("Преобразована в аудит"),
    converted_to_audit: uiText("Преобразована в аудит"),
    closed: uiText("Закрыта"),
  },
  plan: {
    draft: uiText("Черновик"),
    approved: uiText("Утверждён"),
    archived: uiText("В архиве"),
  },
  program: {
    draft: uiText("Черновик"),
    sent: uiText("Направлена"),
    agreed: uiText("Согласована"),
    active: uiText("В работе"),
    completed: uiText("Завершена"),
  },
};

const AUDIT_TYPE_LABELS = {
  internal_operational: uiText("Внутренний операционный аудит"),
  special: uiText("Специальная проверка"),
  compliance: uiText("Аудит соответствия"),
  capability: uiText("Аудит производственных мощностей"),
  management_system: uiText("Аудит системы менеджмента"),
  extended_initial: uiText("Первичный аудит по расширенному регламенту"),
  extended_regular: uiText("Плановый аудит по расширенному регламенту"),
  extended_unplanned: uiText("Внеплановый аудит по расширенному регламенту"),
  extended_thematic: uiText("Тематический аудит по расширенному регламенту"),
  supplier_conformity: uiText("Аудит соответствия поставщика"),
  equipment_rbi: uiText("Аудит оборудования (RBI)"),
  franchise_regular: uiText("Плановый франчайзинговый аудит"),
  pressure_vessel: uiText("Аудит оборудования под давлением"),
};

const ENTITY_TYPE_LABELS = {
  auth: uiText("Авторизация"),
  audit: uiText("Аудит"),
  audit_answer: uiText("Ответ на вопрос аудита"),
  issue: uiText("Замечание"),
  issue_message: uiText("Сообщение по замечанию"),
  company: uiText("Компания"),
  user: uiText("Пользователь"),
  template: uiText("Шаблон"),
  analytics_task: uiText("Аналитическая задача"),
  analytics_form_template: uiText("Шаблон аналитической формы"),
  audit_plan: uiText("План аудита"),
  audit_program: uiText("План аудита"),
  approved_audit_plan: uiText("Утверждённый план аудита"),
  audit_report: uiText("Отчёт аудита"),
  corrective_action: uiText("Корректирующее действие"),
  corrective_plan: uiText("План корректирующих действий"),
  invitation: uiText("Приглашение"),
  registration_application: uiText("Заявка на регистрацию"),
  outgoing_email: uiText("Исходящее письмо"),
  self_check: uiText("Самооценка"),
  file: uiText("Файл"),
  saved_view: uiText("Сохранённое представление"),
  search: uiText("Поиск"),
};

const AUDIT_ACTION_LABELS = {
  login_success: uiText("Успешный вход"),
  login_failed: uiText("Неудачная попытка входа"),
  issue_auto_escalated: uiText("Автоматическая эскалация замечания"),
  create: uiText("Создание"),
  update: uiText("Изменение"),
  delete: uiText("Удаление"),
  upload_file: uiText("Загрузка файла"),
  issue_updated: uiText("Изменение замечания"),
  user_updated: uiText("Изменение пользователя"),
  admin_password_reset: uiText("Сброс пароля администратором"),
  force_password_change: uiText("Запрос смены пароля"),
  sessions_revoked: uiText("Завершение сеансов"),
  restore_from_recycle_bin: uiText("Восстановление из корзины"),
};

const STATUS_VARIANTS = {
  not_started: 'neutral',
  draft: 'neutral',
  cancelled: 'neutral',
  closed: 'success',
  suspended: 'warning',
  in_progress: 'info',
  program_sent: 'info',
  program_confirmed: 'info',
  submitted: 'info',
  approved: 'success',
  ready: 'warning',
  awaiting_closure: 'warning',
  awaiting_customer: 'warning',
  in_review: 'warning',
  completed: 'success',
  resolved: 'success',
  rejected: 'danger',
  open: 'danger',
};

const STATUS_EXPLANATIONS = {
  audit: {
    draft: uiText("Карточка подготовлена, но аудит ещё не запущен."),
    not_started: uiText("Аудит запланирован. Аудитор может начать работу."),
    program_sent: uiText("Программа направлена проверяемой стороне и ожидает решения."),
    program_confirmed: uiText("Программа согласована. Можно переходить к проведению аудита."),
    in_progress: uiText("Аудит проводится, чек-лист и материалы доступны для работы."),
    ready: uiText("Все обязательные действия выполнены. Аудитор может закрыть аудит."),
    awaiting_closure: uiText("Все обязательные действия выполнены. Аудитор может закрыть аудит."),
    completed: uiText("Работа по аудиту завершена."),
    closed: uiText("Аудит формально закрыт и хранится в истории."),
    suspended: uiText("Работа временно остановлена до возобновления аудитором."),
    cancelled: uiText("Аудит отменён и больше не требует действий."),
  },
  issue: {
    open: uiText("Замечание зарегистрировано и ожидает реакции."),
    in_progress: uiText("Ответственный устраняет замечание."),
    fixed: uiText("Исполнение заявлено и ожидает проверки аудитора."),
    verified: uiText("Аудитор подтвердил устранение замечания."),
    reopened: uiText("Замечание возвращено в работу."),
    escalated: uiText("Срок нарушен — требуется решение ответственного."),
    resolved: uiText("Несоответствие устранено."),
    closed: uiText("Работа по замечанию завершена."),
  },
  plan: {
    draft: uiText("План можно редактировать до утверждения."),
    approved: uiText("План утверждён и доступен для исполнения."),
    archived: uiText("План перенесён в архив."),
  },
  program: {
    draft: uiText("Программа ещё редактируется."),
    sent: uiText("Программа направлена на согласование."),
    agreed: uiText("Программа согласована участниками."),
    active: uiText("Аудит проводится по этой программе."),
    completed: uiText("Все мероприятия программы завершены."),
  },
};

export function formatStatus(status, type = 'audit') {
  return STATUS_LABELS[type]?.[status] || status || '—';
}

export function formatAuditType(value) {
  return AUDIT_TYPE_LABELS[value] || value || '—';
}

export function formatEntityType(value) {
  return ENTITY_TYPE_LABELS[value] || value || '—';
}

export function formatAuditAction(value) {
  return AUDIT_ACTION_LABELS[value] || value || '—';
}

export function getStatusVariant(status) {
  return STATUS_VARIANTS[status] || 'neutral';
}

export function getStatusExplanation(status, type = 'audit') {
  return STATUS_EXPLANATIONS[type]?.[status] || '';
}

export function getFullName(entity, fallback = '—') {
  if (!entity) return fallback;
  return entity.full_name || entity.name || entity.title || fallback;
}

export function isOverdue(value) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}
